import React, { useState } from "react";
import PropTypes from "prop-types";
import { FaChevronRight } from "react-icons/fa";
import "./TreeTable.scss";

/**
 * TreeTable — rows that open into their children, with the same columns at every
 * level (channel → campaign → placement → ad). The expander is a real button with
 * `aria-expanded`, and looks like one — a label that silently expands is a trap
 * (ANALYTICS-CONSOLIDATION-SPEC §3.4).
 *
 * rows: [{ id, label, sub?, color?, muted?, cells: { [columnKey]: node }, children?: rows }]
 */
function Row({ row, level, columns, open, toggle, template }) {
  const hasKids = row.children && row.children.length > 0;
  const isOpen = open.has(row.id);
  return (
    <>
      <div
        className={`tree-table__row tree-table__row--l${Math.min(level, 3)}${row.muted ? " tree-table__row--muted" : ""}${hasKids ? " tree-table__row--parent" : ""}`}
        style={{ gridTemplateColumns: template }}
        role="row"
        onClick={hasKids ? () => toggle(row.id) : undefined}
      >
        <div className="tree-table__label" role="cell" style={{ paddingLeft: 8 + level * 20 }}>
          {hasKids ? (
            <button
              type="button"
              className={`tree-table__toggle${isOpen ? " tree-table__toggle--open" : ""}`}
              aria-expanded={isOpen}
              aria-label={`${isOpen ? "Collapse" : "Expand"} ${typeof row.label === "string" ? row.label : "row"}`}
              onClick={(e) => { e.stopPropagation(); toggle(row.id); }}
            >
              <FaChevronRight />
            </button>
          ) : <span className="tree-table__toggle-space" />}
          {row.color && <i className="tree-table__dot" style={{ background: row.color }} />}
          <span className="tree-table__text" title={row.title || undefined}>
            <span className="tree-table__name">{row.label}</span>
            {row.sub && <span className="tree-table__sub">{row.sub}</span>}
          </span>
          {hasKids && <span className="tree-table__count">{row.children.length}</span>}
        </div>
        {columns.map((c) => (
          <div key={c.key} role="cell" className={`tree-table__cell tree-table__cell--${c.align || "right"}`}>
            {row.cells?.[c.key]}
          </div>
        ))}
      </div>
      {hasKids && isOpen && row.children.map((ch) => (
        <Row key={ch.id} row={ch} level={level + 1} columns={columns} open={open} toggle={toggle} template={template} />
      ))}
    </>
  );
}

const collectIds = (rows) => rows.flatMap((r) => (r.children?.length ? [r.id, ...collectIds(r.children)] : []));

const TreeTable = ({ columns, rows, labelHeader, labelWidth = "minmax(220px, 2.2fr)", defaultOpen = [], emptyText }) => {
  const [open, setOpen] = useState(() => new Set(defaultOpen));
  const toggle = (id) => setOpen((prev) => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const template = [labelWidth, ...columns.map((c) => c.width || "minmax(70px, 1fr)")].join(" ");
  const all = collectIds(rows);
  return (
    <div className="tree-table" role="table">
      <div className="tree-table__head" style={{ gridTemplateColumns: template }} role="row">
        <div className="tree-table__label" role="columnheader">
          {labelHeader}
          {all.length > 0 && (
            <button type="button" className="tree-table__all" onClick={() => setOpen(open.size ? new Set() : new Set(all))}>
              {open.size ? "Collapse all" : "Expand all"}
            </button>
          )}
        </div>
        {columns.map((c) => (
          <div key={c.key} role="columnheader" className={`tree-table__cell tree-table__cell--${c.align || "right"}`} title={c.help || undefined}>
            {c.label}
          </div>
        ))}
      </div>
      {rows.length === 0 && <div className="tree-table__empty">{emptyText || "Nothing in this period."}</div>}
      {rows.map((r) => (
        <Row key={r.id} row={r} level={0} columns={columns} open={open} toggle={toggle} template={template} />
      ))}
    </div>
  );
};

TreeTable.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired, label: PropTypes.node.isRequired, align: PropTypes.oneOf(["left", "right", "center"]),
    width: PropTypes.string, help: PropTypes.string,
  })).isRequired,
  rows: PropTypes.array.isRequired,
  labelHeader: PropTypes.node,
  labelWidth: PropTypes.string,
  defaultOpen: PropTypes.array,
  emptyText: PropTypes.node,
};

export default TreeTable;
